// W36: thin wrapper over the generated feature-flags surface.
// W26: feature-flag CRUD lives in the settings service.
// `/v1/admin/feature-flags/*` is the operator surface; consumer services read
// effective state directly (helper TBD in a sibling package).
//
// Types come from `./generated/settings`: the same OpenAPI document that backs
// `./settings.ts`, since `AdminFeatureFlagsEndpoints` is part of
// `services/settings`. The request path stays on the house core so the
// envelope, Idempotency-Key, token refresh and ApiError mapping behave exactly
// as before.
//
// ⚠ Path note; the list is registered as
// `MapGroup("/v1/admin/feature-flags")` + `MapGet("/")`, so the ASP.NET route
// pattern carries a trailing slash (`/v1/admin/feature-flags/`). The emitted
// OpenAPI document normalises that away, so
// `gen.SettingsAdminListFeatureFlagsData['url']` reads `/v1/admin/feature-flags`
//: exactly the string `list()` has always sent. Routing accepts both spellings
// (both answer 401 rather than 404), so every URL below is left byte-identical
// to what shipped.

import { http } from './_core/request.js';
import type { Envelope } from './_core/envelope.js';
import type * as gen from './generated/settings/index.js';

// ── Types (generated) ──────────────────────────────────────────────────────

/**
 * One scoped override attached to a flag. `scope_id` is the numeric scope
 * (global 1 · country 2 · org 3 · sub_account 4 · phone 5 · user 6) that
 * `SettingScope.Parse` turned the request's `scope_code` into.
 *
 * ⚠ DRIFT: the service sets `DefaultIgnoreCondition = WhenWritingNull`
 * (`services/settings/Program.cs`), so `reason` and `created_by` are *absent*
 * from the JSON when null rather than present-and-null. The generated
 * `null | string` is kept: it already forces callers through the same guard an
 * `undefined` would, and every console call site uses `??` or a truthiness
 * check.
 */
export type FeatureFlagOverride = gen.FeatureFlagOverrideDto;

/**
 * A flag with its overrides inlined: list and detail share the shape.
 *
 * ⚠ DRIFT: `updated_at` and `updated_by` were declared optional here. The
 * handler projects both unconditionally (`updated_at` from a non-nullable
 * `DateTimeOffset`), so the generated required members are the truth; no
 * console reads either field, so they are corrected rather than pinned. The
 * `WhenWritingNull` caveat on `FeatureFlagOverride` applies to `description`,
 * `owner` and `updated_by` here too.
 */
export type FeatureFlag = gen.FeatureFlagDto;

/**
 * Body of `PUT /v1/admin/feature-flags/{key}`.
 *
 * ⚠ DRIFT: the generated `UpsertFlagRequest` marks every member optional,
 * because the C# DTO declares `DefaultEnabled`/`RolloutPercent` as
 * non-nullable value types that model binding silently defaults. Omitting them
 * therefore writes `false` / `0` rather than leaving the stored values alone,
 * so both stay required here; which is also the signature `apps/admin-ui`
 * compiles against.
 */
export type UpsertFeatureFlagRequest = Omit<
  gen.UpsertFlagRequest,
  'default_enabled' | 'rollout_percent'
> & {
  default_enabled: boolean;
  rollout_percent: number;
};

/**
 * Body of `PUT /v1/admin/feature-flags/{key}/overrides`.
 *
 * `scope_code` is one of `global | country | org | sub_account | phone | user`
 * (`services/settings/Domain/SettingEntry.cs` → `SettingScope.Parse`; anything
 * else is a 400 `UNKNOWN_SCOPE`). It stays a plain `string` rather than the
 * `SettingScope` union because `apps/admin-ui` feeds it from a `string` select
 * state: narrowing it would break `EditFlagDrawer`.
 *
 * ⚠ DRIFT: the generated `UpsertOverrideRequest` marks every member optional;
 * the handler rejects a blank `scope_code` or `scope_target` with a 400
 * `INVALID_SCOPE`, and an omitted `enabled` silently means "off", so all three
 * stay required.
 */
export type UpsertOverrideRequest = Omit<
  gen.UpsertOverrideRequest,
  'scope_code' | 'scope_target' | 'enabled'
> & {
  scope_code: string;
  scope_target: string;
  enabled: boolean;
};

/** Query of `GET /v1/admin/feature-flags`, straight off the generated shape. */
export type FeatureFlagListOpts = NonNullable<gen.SettingsAdminListFeatureFlagsData['query']>;

// ── Operations ─────────────────────────────────────────────────────────────

export function list(opts?: FeatureFlagListOpts): Promise<Envelope<FeatureFlag[]>> {
  const q = opts?.include_archived ? { include_archived: true } : undefined;
  return http.get<Envelope<FeatureFlag[]>>('/v1/admin/feature-flags', q ? { query: q } : undefined);
}

export function get(key: string): Promise<Envelope<FeatureFlag>> {
  return http.get<Envelope<FeatureFlag>>(`/v1/admin/feature-flags/${encodeURIComponent(key)}`);
}

export function upsert(key: string, req: UpsertFeatureFlagRequest): Promise<Envelope<FeatureFlag>> {
  return http.put<Envelope<FeatureFlag>>(`/v1/admin/feature-flags/${encodeURIComponent(key)}`, { body: req });
}

export function remove(key: string): Promise<void> {
  return http.delete<void>(`/v1/admin/feature-flags/${encodeURIComponent(key)}`);
}

export function upsertOverride(key: string, req: UpsertOverrideRequest): Promise<Envelope<FeatureFlagOverride>> {
  return http.put<Envelope<FeatureFlagOverride>>(`/v1/admin/feature-flags/${encodeURIComponent(key)}/overrides`, { body: req });
}

export function deleteOverride(key: string, id: string): Promise<void> {
  return http.delete<void>(`/v1/admin/feature-flags/${encodeURIComponent(key)}/overrides/${encodeURIComponent(id)}`);
}
